import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  ListItemIcon,
  ListItemText,
  Badge,
  Alert,
  Snackbar,
  Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Build as BuildIcon,
  Description as RequestIcon,
  ShoppingCart as SalesIcon,
  Assessment as ReportsIcon,
  Email as MessageIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  ExpandLess,
  ExpandMore,
  Person as PersonIcon,
  ExitToApp as LogoutIcon,
  Description as DescriptionIcon,
  ShoppingCart as ShoppingCartIcon,
  Replay as ReplayIcon,
  Forum as ForumIcon,
  Create as CreateIcon,
  PhoneAndroid as PhoneAndroidIcon,
  LibraryBooks as LibraryBooksIcon,
  Category as CategoryIcon,
  LocalShipping as LocalShippingIcon,
  MenuBook as MenuBookIcon
} from '@mui/icons-material';
import { hasRouteAccess, SYSTEM_CONSOLE_DEFAULT_PATH } from '../config/routes';
import { buildNavMenuItems, canAccessNavSubItem } from '../config/navMenu';
import { useNotifications } from '../contexts/NotificationContext';

const submenuDomId = (title) =>
  `navbar-submenu-${String(title).replace(/\s+/g, '-').replace(/[^\w\-а-яА-ЯёЁ]/gi, '')}`;

const inboxCountBadgeSx = {
  ml: 2,
  flexShrink: 0,
  '& .MuiBadge-badge': {
    position: 'static',
    transform: 'none',
  },
};

const focusRingSx = {
  position: 'relative',
  '&:focus-visible': {
    outline: 'none',
  },
  '&:focus-visible::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: 'inherit',
    border: '2px solid rgba(255, 255, 255, 0.95)',
    boxSizing: 'border-box',
    pointerEvents: 'none',
  },
};

// Компонент для подменю
const SubMenu = ({ item }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const location = useLocation();
  const { user } = useAuth();
  const menuId = submenuDomId(item.title);
  
  // Фильтруем подменю, оставляя только пункты, к которым есть доступ
  const filteredSubmenu = item.submenu.filter((subItem) => canAccessNavSubItem(user, subItem));

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  // Если нет доступных пунктов подменю, не отображаем кнопку вообще
  if (filteredSubmenu.length === 0) {
    return null;
  }

  return (
    <>
      <Button
        color="inherit"
        onClick={handleClick}
        startIcon={item.icon ? React.createElement(item.icon) : undefined}
        endIcon={anchorEl ? <ExpandLess /> : <ExpandMore />}
        id={`${menuId}-button`}
        aria-controls={menuId}
        aria-haspopup="true"
        aria-expanded={anchorEl ? 'true' : 'false'}
        size="small"
        sx={{
          minWidth: 'auto',
          fontSize: '0.8125rem',
          px: 1,
          py: 0.5,
          borderRadius: 2,
          ...focusRingSx,
          '& .MuiButton-startIcon': { marginLeft: 0, '& > *:nth-of-type(1)': { fontSize: '1.125rem' } },
          '& .MuiButton-endIcon': { marginRight: 0, '& > *:nth-of-type(1)': { fontSize: '1.125rem' } },
          backgroundColor: filteredSubmenu.some(subItem => isActive(subItem.path))
            ? alpha('#fff', 0.08)
            : 'transparent'
        }}
      >
        {item.title}
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        MenuListProps={{ 'aria-labelledby': `${menuId}-button` }}
      >
        {filteredSubmenu.map(subItem => (
          <MenuItem
            key={subItem.path}
            component={Link}
            to={subItem.path}
            onClick={handleClose}
            selected={isActive(subItem.path)}
          >
            <ListItemIcon>{subItem.icon ? React.createElement(subItem.icon) : null}</ListItemIcon>
            <ListItemText primary={subItem.title} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { unreadMessages, unreadNotifications } = useNotifications();
  const msgCount = Math.max(0, Number(unreadMessages) || 0);
  const notifCount = Math.max(0, Number(unreadNotifications) || 0);
  const inboxTotalCount = msgCount + notifCount;

  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState(null);
  const [dropdownAnchor, setDropdownAnchor] = useState({});
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [inboxMenuAnchor, setInboxMenuAnchor] = useState(null);
  const [error, setError] = useState('');

  // --- Динамическое определение мобильного режима ---
  // Замеряем натуральную ширину кнопок навигации и сравниваем
  // с доступным в Toolbar пространством.
  const menuBoxRef = useRef(null);
  const rightBoxRef = useRef(null);
  const [overflowed, setOverflowed] = useState(false);
  const menuContentWidthRef = useRef(0);
  const overflowedRef = useRef(false);

  const checkOverflow = useCallback(() => {
    const box = menuBoxRef.current;
    if (box && !overflowedRef.current) {
      const children = box.children;
      if (children.length > 0) {
        const gap = parseFloat(getComputedStyle(box).columnGap) || 0;
        let w = 0;
        for (let i = 0; i < children.length; i++) {
          w += children[i].offsetWidth;
        }
        w += gap * Math.max(0, children.length - 1);
        if (w > 0) menuContentWidthRef.current = w;
      }
    }

    const rightW = rightBoxRef.current ? rightBoxRef.current.offsetWidth : 150;
    const titleW = 80;
    const margins = 48;
    const needed = menuContentWidthRef.current + rightW + titleW + margins;
    const shouldOverflow = needed > window.innerWidth;

    overflowedRef.current = shouldOverflow;
    setOverflowed(shouldOverflow);
  }, []);

  useEffect(() => {
    requestAnimationFrame(checkOverflow);
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [checkOverflow]);

  useEffect(() => {
    requestAnimationFrame(checkOverflow);
  }, [location.pathname, user?.profile_type_code, checkOverflow]);

  const isMobile = overflowed;

  const mobileMenuOpen = Boolean(mobileMenuAnchorEl);

  const handleDropdownOpen = (event, menuId) => {
    setDropdownAnchor(prev => ({
      ...prev,
      [menuId]: event.currentTarget
    }));
  };

  const handleDropdownClose = (menuId) => {
    setDropdownAnchor(prev => ({
      ...prev,
      [menuId]: null
    }));
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleInboxMenuOpen = (event) => {
    setInboxMenuAnchor(event.currentTarget);
  };

  const handleInboxMenuClose = () => {
    setInboxMenuAnchor(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (_err) {
      setError('Ошибка при выходе из системы');
    } finally {
      handleUserMenuClose();
    }
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const iconMap = {
    HomeIcon,
    BuildIcon,
    RequestIcon,
    SalesIcon,
    ShoppingCartIcon,
    DescriptionIcon,
    CreateIcon,
    LibraryBooksIcon,
    ReportsIcon,
    PersonIcon,
    CategoryIcon,
    PhoneAndroidIcon,
    LocalShippingIcon,
    MenuBookIcon,
    ReplayIcon,
    SettingsIcon,
  };
  const { customerMenuItems, standardMenuItems } = buildNavMenuItems(iconMap);

  const isConsoleOperator = user?.profile_type_code === 'system_console';

  // Выбираем подходящий набор меню в зависимости от типа профиля
  const menuToUse = user.profile_type_code === 'customer' ? customerMenuItems : standardMenuItems;

  // Фильтруем пункты меню без мутации исходных объектов (устойчивость к повторным рендерам / Strict Mode)
  const filteredMenu = isConsoleOperator
    ? [
        {
          title: 'Системная консоль',
          path: SYSTEM_CONSOLE_DEFAULT_PATH,
          icon: SettingsIcon,
        },
      ]
    : menuToUse.reduce((acc, item) => {
        if (item.submenu) {
          const filteredSubmenu = item.submenu.filter((subItem) => canAccessNavSubItem(user, subItem));
          if (filteredSubmenu.length > 0) {
            acc.push({ ...item, submenu: filteredSubmenu });
          }
          return acc;
        }
        if (hasRouteAccess(item.path, user.profile_type_code)) {
          acc.push(item);
        }
        return acc;
      }, []);

  const renderDesktopItems = () =>
    filteredMenu.map(item => (
      item.submenu ? (
        <SubMenu key={item.title} item={item} />
      ) : (
        <Button
          key={item.title}
          color="inherit"
          component={Link}
          to={item.path}
          startIcon={item.icon ? React.createElement(item.icon) : undefined}
          size="small"
          sx={{
            minWidth: 'auto',
            fontSize: '0.8125rem',
            px: 1,
            py: 0.5,
            borderRadius: 2,
            ...focusRingSx,
            '& .MuiButton-startIcon': { marginLeft: 0, '& > *:nth-of-type(1)': { fontSize: '1.125rem' } },
            '& .MuiButton-endIcon': { marginRight: 0 },
            backgroundColor: isActive(item.path) 
              ? alpha('#fff', 0.08)
              : 'transparent'
          }}
        >
          {item.title}
        </Button>
      )
    ));

  const renderMobileMenu = () => (
    <Menu
      id="navbar-mobile-menu"
      anchorEl={mobileMenuAnchorEl}
      open={mobileMenuOpen}
      onClose={() => setMobileMenuAnchorEl(null)}
      sx={{ mt: 5 }}
    >
      {filteredMenu.map(item => (
        item.submenu ? (
          <div key={item.title}>
            <MenuItem onClick={(e) => handleDropdownOpen(e, item.title)}>
              <ListItemIcon>{item.icon ? React.createElement(item.icon) : null}</ListItemIcon>
              <ListItemText primary={item.title} />
              {dropdownAnchor[item.title] ? <ExpandLess /> : <ExpandMore />}
            </MenuItem>
            <Menu
              anchorEl={dropdownAnchor[item.title]}
              open={Boolean(dropdownAnchor[item.title])}
              onClose={() => handleDropdownClose(item.title)}
            >
              {item.submenu.map(subItem => (
                <MenuItem
                  key={subItem.path}
                  component={Link}
                  to={subItem.path}
                  onClick={() => {
                    handleDropdownClose(item.title);
                    setMobileMenuAnchorEl(null);
                  }}
                >
                  <ListItemText primary={subItem.title} />
                </MenuItem>
              ))}
            </Menu>
          </div>
        ) : (
          <MenuItem
            key={item.path || item.title}
            component={Link}
            to={item.path}
            onClick={() => setMobileMenuAnchorEl(null)}
          >
            <ListItemIcon>{item.icon ? React.createElement(item.icon) : null}</ListItemIcon>
            <ListItemText primary={item.title} />
          </MenuItem>
        )
      ))}
    </Menu>
  );

  return (
    <AppBar position="fixed">
      <Toolbar
        sx={{
          flexWrap: 'nowrap',
          alignItems: 'center',
          overflow: 'visible',
        }}
      >
        {isMobile && (
          <IconButton
            color="inherit"
            edge="start"
            sx={{
              mr: 1,
              minWidth: 48,
              minHeight: 48,
              borderRadius: 2,
              ...focusRingSx,
            }}
            aria-label="Открыть главное меню"
            aria-controls="navbar-mobile-menu"
            aria-expanded={mobileMenuOpen}
            onClick={(event) => {
              setMobileMenuAnchorEl((prev) => (prev ? null : event.currentTarget));
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        
        <Typography 
          variant="subtitle2" 
          component={Link} 
          to={isConsoleOperator ? SYSTEM_CONSOLE_DEFAULT_PATH : '/'}
          sx={{ 
            flexGrow: isMobile ? 1 : 0,
            flexShrink: isMobile ? 1 : 10,
            minWidth: 0,
            textDecoration: 'none',
            color: 'inherit',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: 1.2,
          }}
        >
          {user?.profile_type_code === 'customer' ? 'Клиент' : 
           user?.profile_type_code === 'depot' ? 'Сервисный центр' : 
           user?.profile_type_code === 'company' ? 'Сервисная компания' : 
           user?.profile_type_code === 'system_console' ? 'Системная консоль' :
           'Приложение "Сервис"'}
        </Typography>

        {/* Десктопное меню. Замер ширины кнопок при десктопном режиме,
            сохраняется в menuContentWidthRef для расчётов в мобильном. */}
        {!isMobile && (
          <Box
            ref={menuBoxRef}
            sx={{
              flexGrow: 1,
              minWidth: 0,
              display: 'flex',
              gap: 0.5,
              ml: 1,
              justifyContent: 'center',
              flexWrap: 'nowrap',
              overflowX: 'hidden',
              whiteSpace: 'nowrap',
              '& > *': { position: 'relative' },
            }}
          >
            {renderDesktopItems()}
          </Box>
        )}

        {isMobile && renderMobileMenu()}

        <Box ref={rightBoxRef} sx={{ flexGrow: 0, flexShrink: 0, display: 'flex', gap: 0.5 }}>
          {user?.profile_type_code !== 'customer' && !isConsoleOperator && (
            <Tooltip title="Поиск">
              <IconButton
                color="inherit"
                component={Link}
                to="/search"
                aria-label="Поиск"
                sx={{ borderRadius: 2, ...focusRingSx }}
              >
                <SearchIcon />
              </IconButton>
            </Tooltip>
          )}
          {!isConsoleOperator && (
            <>
              <Tooltip title={inboxTotalCount > 0 ? `Входящие: ${inboxTotalCount}` : 'Входящие'}>
                <IconButton
                  color="inherit"
                  onClick={handleInboxMenuOpen}
                  aria-label={inboxTotalCount > 0 ? `Входящие: ${inboxTotalCount}` : 'Входящие'}
                  aria-haspopup="true"
                  aria-expanded={inboxMenuAnchor ? 'true' : 'false'}
                  aria-controls="inbox-menu"
                  sx={{ borderRadius: 2, ...focusRingSx }}
                >
                  <Badge
                    badgeContent={inboxTotalCount}
                    color="error"
                    invisible={inboxTotalCount === 0}
                    max={99}
                  >
                    <ForumIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Menu
                id="inbox-menu"
                anchorEl={inboxMenuAnchor}
                open={Boolean(inboxMenuAnchor)}
                onClose={handleInboxMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{ sx: { mt: 1, minWidth: 260 } }}
              >
                <MenuItem
                  component={Link}
                  to="/messages/inbox"
                  onClick={handleInboxMenuClose}
                >
                  <ListItemIcon><MessageIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Сообщения" />
                  {msgCount > 0 && (
                    <Badge badgeContent={msgCount} color="error" max={99} sx={inboxCountBadgeSx} />
                  )}
                </MenuItem>
                <MenuItem
                  component={Link}
                  to="/notifications"
                  onClick={handleInboxMenuClose}
                >
                  <ListItemIcon><NotificationsIcon fontSize="small" /></ListItemIcon>
                  <ListItemText primary="Уведомления" />
                  {notifCount > 0 && (
                    <Badge badgeContent={notifCount} color="error" max={99} sx={inboxCountBadgeSx} />
                  )}
                </MenuItem>
              </Menu>
            </>
          )}
          <IconButton
            color="inherit"
            onClick={handleUserMenuOpen}
            sx={{ borderRadius: 2, ...focusRingSx }}
            aria-label="Меню аккаунта"
            aria-haspopup="true"
            aria-expanded={userMenuAnchor ? 'true' : 'false'}
            aria-controls="user-menu"
          >
            <PersonIcon />
          </IconButton>
        </Box>

        <Menu
          id="user-menu"
          anchorEl={userMenuAnchor}
          keepMounted
          open={Boolean(userMenuAnchor)}
          onClose={handleUserMenuClose}
          PaperProps={{
            sx: {
              mt: 1.5,
              width: 200
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {user?.name || user?.login}
            </Typography>
          </MenuItem>
          {!isConsoleOperator && (
            <Tooltip title={!(user && user.profile_uid) ? 'Профиль недоступен' : ''}>
              <span>
                <MenuItem
                  component={user && user.profile_uid ? Link : 'div'}
                  to={user && user.profile_uid ? `/profiles/${user.profile_uid}` : undefined}
                  onClick={handleUserMenuClose}
                  disabled={!(user && user.profile_uid)}
                >
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Мой профиль" />
                </MenuItem>
              </span>
            </Tooltip>
          )}
          {!isConsoleOperator && (
            <MenuItem
              component={Link}
              to="/settings"
              onClick={handleUserMenuClose}
            >
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Настройки" />
            </MenuItem>
          )}
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Выход" />
          </MenuItem>
        </Menu>
      </Toolbar>

      <Snackbar 
        open={Boolean(error)} 
        autoHideDuration={6000} 
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError('')} role="alert" variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </AppBar>
  );
}

export default Navbar; 